import { GlobalProfileState } from '@/composition/globalProfile'
import { MediaState } from '@/composition/medias'
import { PostState } from '@/composition/posts'
import { emitter } from '@/main'
import { IComments } from '@/models/Comments'
import { IPosts, PostType } from '@/models/Posts'
import { getHostName } from '@ghl/clientportal-core'
import { Router } from 'vue-router'

export function handleCardClick(post: IPosts, router: Router): void {
  MediaState.leaveAllStreams()
  if (post.postType === PostType.GO_LIVE) {
    router.push({
      name: 'group-post-view',
      params: {
        postId: post._id,
      },
    })
  } else if (
    router.currentRoute.value.name === 'home' ||
    router.currentRoute.value.name === 'to-review'
  ) {
    router.push({
      name: 'group-post-view',
      params: {
        postId: post._id,
      },
    })
  } else if (router.currentRoute.value.name === 'channel-overview') {
    router.push({
      name: 'channel-post-view',
      params: {
        postId: post._id,
      },
    })
  } else if (router.currentRoute.value.name === 'group-home-search') {
    /** forward the query params to the new route */
    const query = router.currentRoute.value.query
    router.push({
      name: 'group-search-post-view',
      params: {
        postId: post._id,
      },
      query: { ...query },
    })
  } else if (router.currentRoute.value.name === 'channel-search') {
    /** forward the query params to the new route */
    const query = router.currentRoute.value.query
    router.push({
      name: 'channel-search-post-view',
      params: {
        postId: post._id,
      },
      query: { ...query },
    })
  } else if (router.currentRoute.value.name === 'global-user-profile') {
    const query = router.currentRoute.value.query
    router.push({
      name: 'global-user-profile-post-view',
      params: {
        postId: post._id,
      },
      query,
    })
  } else {
    router.push({
      name: 'user-post-view',
      params: {
        postId: post._id,
        userSlug: post.user.slug,
      },
    })
  }
}

export function handleCommentCardClick(
  comment: IComments,
  router: Router
): void {
  const query = router.currentRoute.value.query
  if (router.currentRoute.value.name === 'group-home-search') {
    router.push({
      name: 'group-search-post-view',
      params: {
        postId: comment.postId,
      },
      query: {
        ...query,
        commentId: comment._id,
        parentId: comment.parentId,
        modal: 'true',
      },
    })
  } else if (router.currentRoute.value.name === 'channel-search') {
    router.push({
      name: 'channel-search-post-view',
      params: {
        postId: comment.postId,
      },
      query: {
        ...query,
        commentId: comment._id,
        parentId: comment.parentId,
        modal: 'true',
      },
    })
  } else if (router.currentRoute.value.name === 'global-user-profile') {
    router.push({
      name: 'global-user-profile-post-view',
      params: {
        postId: comment.postId,
      },
      query: {
        ...query,
        commentId: comment._id,
        parentId: comment.parentId,
      },
    })
  }
}

export async function handleShareClick(
  post: IPosts,
  router: Router
): Promise<void> {
  let postLink = `https://${getHostName()}${
    router.currentRoute.value.fullPath
  }/posts/${post._id}`
  const currentRoute = router.currentRoute.value
  if (
    currentRoute.name &&
    [
      'channel-post-view',
      'group-post-view',
      'user-post-view',
      'channel-search-post-view',
      'group-search-post-view',
      'global-user-profile-post-view',
    ].includes(currentRoute.name as string)
  ) {
    postLink = `https://${getHostName()}${currentRoute.fullPath}`
  } else if (currentRoute.name === 'global-user-profile') {
    const pathWithoutQuery = currentRoute.path
    const queryString = currentRoute.fullPath.includes('?')
      ? currentRoute.fullPath.substring(currentRoute.fullPath.indexOf('?'))
      : ''
    postLink = `https://${getHostName()}${pathWithoutQuery}/posts/${
      post._id
    }${queryString}`
  }
  await navigator.clipboard.writeText(postLink)
}

export function getShareablePostLink(post: IPosts, router: Router): string {
  let postLink = `https://${getHostName()}${
    router.currentRoute.value.fullPath
  }/posts/${post._id}`
  const currentRoute = router.currentRoute.value
  if (
    currentRoute.name &&
    [
      'channel-post-view',
      'group-post-view',
      'user-post-view',
      'channel-search-post-view',
      'group-search-post-view',
      'global-user-profile-post-view',
    ].includes(currentRoute.name as string)
  )
    postLink = `https://${getHostName()}${currentRoute.fullPath}`
  else if (currentRoute.name === 'global-user-profile') {
    const pathWithoutQuery = currentRoute.path
    const queryString = currentRoute.fullPath.includes('?')
      ? currentRoute.fullPath.substring(currentRoute.fullPath.indexOf('?'))
      : ''
    postLink = `https://${getHostName()}${pathWithoutQuery}/posts/${
      post._id
    }${queryString}`
  }
  return postLink
}

export function getTextContent(htmlContent?: string): string {
  if (htmlContent) {
    return htmlContent
      .replace(/<(?!\/?(a|span)(?=>|\s.*>))[^>]+>/g, '\n')
      .trim()
  } else {
    return ''
  }
}

export function getTextContentForValidation(htmlContent?: string): string {
  if (htmlContent) {
    return htmlContent
      .replace(/<(?!\/?(a|span|img)(?=>|\s.*>))[^>]+>/g, '\n')
      .trim()
  } else {
    return ''
  }
}

export async function deletePost(post: IPosts): Promise<void> {
  if (post.channelPinnedIndex != undefined) {
    await PostState.unpinPostFromChannelTimeline(
      post._id as string,
      post.channelSlug
    )
  }
  if (post.homePinnedIndex != undefined) {
    await PostState.unpinPostFromHomeTimeline(
      post._id as string,
      post.channelId
    )
  }
  await PostState.deletePost(post._id as string)
  const userContributions =
    GlobalProfileState.state.contributions?.contributions ?? []

  const index = userContributions.findIndex(
    contribution => contribution._id === post._id
  )
  if (index !== -1) {
    userContributions.splice(index, 1)
    GlobalProfileState.state.contributions.total -= 1
  }

  emitter.emit('close-postview')
}
